import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/**
 * EmailStatusIcons — SVG edition (Feb 2026)
 *
 * Renders 0-3 compact coloured pills showing the latest Brevo status of the
 * quote / invoice / collection_notice emails for a job.
 *
 * All glyphs are inline SVG (lucide-react shape family) so they render
 * pixel-identically on every browser, OS, and printed report — no more
 * emoji rendering drift between Windows/Mac/mobile/printer.
 *
 * Props:
 *   summary  — { quote?: {...}, invoice?: {...}, collection_notice?: {...} } | null
 *   jobId    — used to fetch the full timeline on click
 *   apiBase  — API base URL (already ends in /api)
 */

// ── Inline SVG icons (14×14 for status, 15×15 for type) ─────────────────
const SvgFileText = () => (
  <svg className="type-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);
const SvgDollar = () => (
  <svg className="type-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);
const SvgAlertTriangle = () => (
  <svg className="type-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const SvgSend = () => (
  <svg className="status-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const SvgCheck = () => (
  <svg className="status-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const SvgEye = () => (
  <svg className="status-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const SvgXCircle = () => (
  <svg className="status-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="15" y1="9" x2="9" y2="15"/>
    <line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);

const TYPE_META = {
  quote:             { Icon: SvgFileText,      label: 'Quote' },
  invoice:           { Icon: SvgDollar,        label: 'Invoice' },
  collection_notice: { Icon: SvgAlertTriangle, label: 'Collection notice' },
};

const STATUS_META = {
  sent:      { Icon: SvgSend,    label: 'Sent',      bg: '#dbeafe', border: '#93c5fd', color: '#1e3a8a' },
  delivered: { Icon: SvgCheck,   label: 'Delivered', bg: '#ccfbf1', border: '#5eead4', color: '#134e4a' },
  opened:    { Icon: SvgEye,     label: 'Opened',    bg: '#dcfce7', border: '#86efac', color: '#14532d' },
  bounced:   { Icon: SvgXCircle, label: 'Bounced',   bg: '#fee2e2', border: '#fca5a5', color: '#7f1d1d' },
};

const ICON_STYLE_BASE = `
  .es-icon svg { width: 14px; height: 14px; display: inline-block; }
  .es-icon .type-svg { width: 15px; height: 15px; }
`;

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      dateStyle: 'medium', timeStyle: 'short', timeZone: 'Australia/Sydney',
    });
  } catch { return iso; }
};

const buildTooltip = (typeMeta, s) => {
  const bits = [`${typeMeta.label} — ${STATUS_META[s.status]?.label || s.status}`];
  if (s.sent_at) bits.push(`Sent ${fmt(s.sent_at)}`);
  if (s.delivered_at) bits.push(`Delivered ${fmt(s.delivered_at)}`);
  if (s.first_opened_at) {
    if (s.open_count > 1) {
      bits.push(`Opened ${s.open_count}× (last ${fmt(s.last_opened_at)})`);
    } else {
      bits.push(`Opened ${fmt(s.first_opened_at)}`);
    }
  }
  if (s.bounce_reason) bits.push(`Bounce: ${s.bounce_reason}`);
  if (s.sent_to) bits.push(`To: ${s.sent_to}`);
  return bits.join(' • ');
};

export const EmailStatusIcons = ({ summary, jobId, apiBase }) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!summary || Object.keys(summary).length === 0) return null;

  const openPopup = async () => {
    setPopupOpen(true);
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/jobs/${jobId}/email-timeline`);
      setTimeline(res.data);
    } catch {
      setTimeline({ emails: [], error: true });
    } finally {
      setLoading(false);
    }
  };

  const order = ['quote', 'invoice', 'collection_notice'];
  const icons = order.filter((t) => summary[t]);

  return (
    <>
      <style>{ICON_STYLE_BASE}</style>
      <span
        style={{ display: 'inline-flex', gap: '3px', alignItems: 'center', marginLeft: '4px' }}
        data-testid={`email-icons-${jobId}`}
      >
        {icons.map((etype) => {
          const s = summary[etype];
          const tMeta = TYPE_META[etype];
          const sMeta = STATUS_META[s.status] || STATUS_META.sent;
          const TypeIcon = tMeta.Icon;
          const StatusIcon = sMeta.Icon;
          return (
            <button
              key={etype}
              type="button"
              className="es-icon"
              onClick={(e) => { e.stopPropagation(); openPopup(); }}
              title={buildTooltip(tMeta, s)}
              data-testid={`email-icon-${etype}-${jobId}`}
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                height: '30px',
                padding: '0 8px',
                borderRadius: '8px',
                background: sMeta.bg,
                border: `1px solid ${sMeta.border}`,
                color: sMeta.color,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <TypeIcon />
              <StatusIcon />
              {s.open_count > 1 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    background: '#166534',
                    color: '#fff',
                    borderRadius: '999px',
                    fontSize: '9px',
                    fontWeight: 700,
                    padding: '1px 4px',
                    minWidth: '14px',
                    textAlign: 'center',
                    border: '1.5px solid #fff',
                    lineHeight: 1,
                  }}
                >
                  {s.open_count}
                </span>
              )}
            </button>
          );
        })}
      </span>

      <Dialog open={popupOpen} onOpenChange={setPopupOpen}>
        <DialogContent style={{ maxWidth: '640px' }} data-testid={`email-timeline-dialog-${jobId}`}>
          <DialogHeader>
            <DialogTitle>
              Email history{timeline?.job_number ? ` — Job #${timeline.job_number}` : ''}
            </DialogTitle>
          </DialogHeader>
          {loading && <p style={{ color: '#64748b' }}>Loading…</p>}
          {!loading && timeline && (
            <>
              {timeline.customer_email && (
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: 0 }}>
                  Customer: <strong>{timeline.customer_name}</strong> — {timeline.customer_email}
                </p>
              )}
              {timeline.emails?.length === 0 ? (
                <p>No emails sent for this job yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '480px', overflowY: 'auto' }}>
                  {timeline.emails.map((e, i) => {
                    const tMeta = TYPE_META[e.email_type] || { Icon: SvgFileText, label: e.email_type };
                    const sMeta = STATUS_META[e.brevo_status] || STATUS_META.sent;
                    const TypeIcon = tMeta.Icon;
                    const StatusIcon = sMeta.Icon;
                    return (
                      <div
                        key={i}
                        className="es-icon"
                        style={{
                          padding: '0.75rem',
                          border: `1px solid ${sMeta.border}`,
                          borderLeft: `4px solid ${sMeta.color}`,
                          borderRadius: '6px',
                          background: sMeta.bg + '55',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <TypeIcon /> {tMeta.label}
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.85em', color: sMeta.color, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <StatusIcon /> {sMeta.label}
                              {e.open_count > 1 ? ` (${e.open_count}× opened)` : ''}
                            </span>
                          </strong>
                          <span style={{ fontSize: '0.8em', color: '#64748b' }}>{fmt(e.sent_at)}</span>
                        </div>
                        {e.subject && (
                          <div style={{ marginTop: '0.4rem', fontSize: '0.85em', color: '#475569' }}>
                            <strong>Subject:</strong> {e.subject}
                          </div>
                        )}
                        <div style={{ marginTop: '0.4rem', fontSize: '0.85em', color: '#475569' }}>
                          <strong>To:</strong> {e.sent_to}
                          {e.cc?.length > 0 && <> · <strong>Cc:</strong> {e.cc.join(', ')}</>}
                          {e.bcc?.length > 0 && <> · <strong>Bcc:</strong> {e.bcc.join(', ')}</>}
                        </div>
                        <div style={{ marginTop: '0.4rem', fontSize: '0.8em', color: '#64748b', fontFamily: 'monospace' }}>
                          {e.delivered_at && <>Delivered: {fmt(e.delivered_at)}<br/></>}
                          {e.first_opened_at && <>First opened: {fmt(e.first_opened_at)}<br/></>}
                          {e.last_opened_at && e.open_count > 1 && <>Last opened: {fmt(e.last_opened_at)}<br/></>}
                          {e.bounce_reason && (
                            <span style={{ color: '#7f1d1d' }}>Bounce reason: {e.bounce_reason}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
